import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { EnterpriseSetting_Key } from '__generated__/graphql-types';

import QueryPersonalizedReorderingDisabledEnterpriseSetting from 'bundles/enterprise-admin-hooks/queries/QueryPersonalizedReorderingDisabledEnterpriseSetting.graphql';
import type {
  QueryPersonalizedReorderingDisabledEnterpriseSettingQuery,
  QueryPersonalizedReorderingDisabledEnterpriseSettingQueryVariables,
} from 'bundles/enterprise-admin-hooks/queries/__generated__/QueryPersonalizedReorderingDisabledEnterpriseSetting';

type UsePersonalizedReorderingDisabledEnterpriseSettingParams = {
  thirdPartyOrganizationId: string | undefined;
};

const SETTING_KEY = EnterpriseSetting_Key.EnterpriseSettingKeyPersonalizedContentReorderingDisabled;

const usePersonalizedReorderingDisabledEnterpriseSetting = ({
  thirdPartyOrganizationId,
}: UsePersonalizedReorderingDisabledEnterpriseSettingParams) => {
  const { data, loading, error, refetch } = useQuery<
    QueryPersonalizedReorderingDisabledEnterpriseSettingQuery,
    QueryPersonalizedReorderingDisabledEnterpriseSettingQueryVariables
  >(QueryPersonalizedReorderingDisabledEnterpriseSetting, {
    variables: {
      thirdPartyOrganizationId: thirdPartyOrganizationId ?? '',
    },
    skip: !thirdPartyOrganizationId,
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
  });

  const isPersonalizedReorderingDisabled = useMemo(() => {
    const raw = data?.EnterpriseSetting?.findByScopeAndKey?.data;
    if (raw == null) {
      return false;
    }
    try {
      const parsed: Record<string, unknown> = JSON.parse(raw);
      return Boolean(parsed[SETTING_KEY]);
    } catch {
      return false;
    }
  }, [data]);

  return {
    isPersonalizedReorderingEnabled: !isPersonalizedReorderingDisabled,
    loading,
    error,
    refetch,
  };
};

export default usePersonalizedReorderingDisabledEnterpriseSetting;
